/**
 * TratamientoDashboard.js
 * Dashboard gamificado estilo Duolingo para seguimiento de tratamientos
 * Muestra racha, progreso, medicinas del día, y botón para reportar olvidos
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { obtenerDashboardTratamiento } from '../services/api';
import ModalReportarOlvido from './ModalReportarOlvido';
import { generarPDFTratamiento } from '../helpers/pdfTratamientoHelper';
import toast from 'react-hot-toast';

// Mapeo de bloques a iconos y nombres amigables
const BLOQUES_INFO = {
  madrugada: { icon: '🌙', nombre: 'Madrugada' },
  mañana: { icon: '🌅', nombre: 'Mañana' },
  mediodía: { icon: '☀️', nombre: 'Mediodía' },
  tarde: { icon: '🌇', nombre: 'Tarde' },
  noche: { icon: '🌙', nombre: 'Noche' },
  nocturno: { icon: '🌑', nombre: 'Nocturno' }
};

export default function TratamientoDashboard() {
  const { id: tratamientoId } = useParams();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOlvidoModal, setShowOlvidoModal] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);

  useEffect(() => {
    cargarDashboard();
  }, [tratamientoId]);

  async function cargarDashboard() {
    setLoading(true);
    setError(null);

    const result = await obtenerDashboardTratamiento(tratamientoId);

    // Debug: Ver qué devuelve el backend
    console.log('Dashboard data:', result);

    setLoading(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setDashboard(result);
  }

  function handleVolver() {
    // Si hay historial, ir atrás; si no, ir al panel de usuario
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/userpanel');
    }
  }

  function handleOlvidoSuccess() {
    // Recargar dashboard para reflejar cambios
    cargarDashboard();
    toast.success('Registrado correctamente');
  }

  async function handleDescargarPDF() {
    if (!dashboard) return;

    setGenerandoPDF(true);
    try {
      await generarPDFTratamiento(tratamientoId, dashboard, dashboard.nombreMascota);
      toast.success('PDF generado correctamente');
    } catch (error) {
      console.error('Error generando PDF:', error);
      toast.error('Error al generar el PDF');
    } finally {
      setGenerandoPDF(false);
    }
  }

  // Formatear fecha para mostrar
  function formatearFecha(fechaStr) {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr + 'T12:00:00');
    return fecha.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f3f4f6'
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div className="spinner-border text-primary" role="status" style={{ width: '48px', height: '48px' }}>
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p style={{ marginTop: '16px', color: '#666' }}>Cargando tratamiento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f3f4f6',
          padding: '20px'
        }}
      >
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '32px',
            textAlign: 'center',
            maxWidth: '400px'
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>😕</div>
          <h3 style={{ marginBottom: '12px', color: '#111' }}>Error</h3>
          <p style={{ color: '#666', marginBottom: '24px' }}>{error}</p>
          <button
            onClick={handleVolver}
            style={{
              padding: '12px 24px',
              backgroundColor: '#4CAF50',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  if (!dashboard) return null;

  const { tratamiento, progreso, recordatoriosHoy, bloquesHoy, terminado } = dashboard;

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        paddingBottom: '100px'
      }}
    >
      {/* Header con gradiente */}
      <div
        style={{
          background: 'linear-gradient(to bottom, #e8f5c8, #c5e8a0)',
          padding: '24px 20px 80px',
          color: '#333'
        }}
      >
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <button
            onClick={handleVolver}
            style={{
              background: '#4CAF50',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              color: '#fff',
              cursor: 'pointer',
              marginBottom: '20px',
              fontSize: '14px'
            }}
          >
            ← Volver
          </button>

          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
            Mi Tratamiento
          </h1>
          <p style={{ opacity: 0.9, fontSize: '14px', marginBottom: '8px' }}>
            {tratamiento.medicamentos?.length || 0} medicamento{tratamiento.medicamentos?.length !== 1 ? 's' : ''}
          </p>

          {/* Fechas del tratamiento */}
          <div style={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            fontSize: '13px',
            opacity: 0.95
          }}>
            <span>📅 Inicio: {formatearFecha(tratamiento.fechaInicio)}</span>
            <span>📅 Fin: {formatearFecha(tratamiento.fechaFin)}</span>
            <span>⏳ Día {tratamiento.diaActual} de {tratamiento.diasTotales}</span>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 20px' }}>
        {/* Card de Racha (superpuesta al header) */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '20px',
            padding: '32px',
            marginTop: '-60px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            textAlign: 'center',
            marginBottom: '24px'
          }}
        >
          {terminado ? (
            <>
              <div style={{ fontSize: '64px', marginBottom: '8px' }}>🎉</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>
                ¡Tratamiento Completado!
              </div>
              <p style={{ color: '#666', marginTop: '8px' }}>
                Felicidades, completaste tu tratamiento
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: '48px', marginBottom: '8px' }}>🔥</div>
              <div
                style={{
                  fontSize: '64px',
                  fontWeight: '800',
                  color: '#00BCD4',
                  lineHeight: 1
                }}
              >
                {tratamiento.racha || 0}
              </div>
              <div style={{ fontSize: '18px', color: '#666', marginTop: '8px' }}>
                {tratamiento.racha === 1 ? 'día' : 'días'} de racha
              </div>
            </>
          )}
        </div>

        {/* Barra de Progreso */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ fontWeight: '600', color: '#111' }}>Progreso</span>
            <span style={{ color: '#00BCD4', fontWeight: '600' }}>{progreso.porcentaje}%</span>
          </div>

          {/* Barra */}
          <div
            style={{
              backgroundColor: '#e5e7eb',
              borderRadius: '100px',
              height: '12px',
              overflow: 'hidden',
              marginBottom: '12px'
            }}
          >
            <div
              style={{
                backgroundColor: '#00BCD4',
                height: '100%',
                borderRadius: '100px',
                width: `${progreso.porcentaje}%`,
                transition: 'width 0.5s ease'
              }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
            <span style={{ color: '#666' }}>
              Día {tratamiento.diaActual} de {tratamiento.diasTotales}
            </span>
            <span style={{ color: '#16a34a' }}>{progreso.mensaje}</span>
          </div>
        </div>

        {/* Medicinas de Hoy */}
        {!terminado && recordatoriosHoy?.length > 0 && (
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}
          >
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#111' }}>
              📋 Medicinas de hoy
            </h3>

            {recordatoriosHoy.map((grupo, idx) => {
              const bloqueInfo = BLOQUES_INFO[grupo.bloque] || { icon: '⏰', nombre: grupo.bloque };

              return (
                <div
                  key={idx}
                  style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: idx < recordatoriosHoy.length - 1 ? '12px' : 0
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '20px' }}>{bloqueInfo.icon}</span>
                    <span style={{ fontWeight: '600', color: '#111' }}>{bloqueInfo.nombre}</span>
                    <span style={{ color: '#666', fontSize: '13px' }}>({grupo.hora})</span>
                  </div>

                  {grupo.medicamentos.map((med, mIdx) => (
                    <div
                      key={mIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        marginBottom: mIdx < grupo.medicamentos.length - 1 ? '8px' : 0,
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>💊</span>
                      <div>
                        <div style={{ fontWeight: '500', color: '#111', fontSize: '14px' }}>
                          {med.nombre}
                        </div>
                        <div style={{ color: '#666', fontSize: '12px' }}>
                          {med.dosis}
                          {med.instrucciones && ` • ${med.instrucciones}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Estadísticas */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#111' }}>
            📊 Estadísticas
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div
              style={{
                backgroundColor: '#f0fdf4',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#16a34a' }}>
                {tratamiento.adherenciaGeneral}%
              </div>
              <div style={{ fontSize: '13px', color: '#166534' }}>Adherencia</div>
            </div>

            <div
              style={{
                backgroundColor: '#E0F7FA',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#00BCD4' }}>
                {tratamiento.mejorRacha || 0}
              </div>
              <div style={{ fontSize: '13px', color: '#006064' }}>Mejor racha</div>
            </div>
          </div>
        </div>

        {/* Botón Olvidé Algo */}
        {!terminado && (
          <button
            onClick={() => setShowOlvidoModal(true)}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: '#fff',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              marginBottom: '16px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#FF5722';
              e.currentTarget.style.color = '#FF5722';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#666';
            }}
          >
            😅 Olvidé algo hoy
          </button>
        )}

        {/* Botón Descargar PDF */}
        <button
          onClick={handleDescargarPDF}
          disabled={generandoPDF}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#fff',
            border: '2px solid #00BCD4',
            borderRadius: '12px',
            cursor: generandoPDF ? 'wait' : 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            color: '#00BCD4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            marginBottom: '24px',
            opacity: generandoPDF ? 0.7 : 1
          }}
          onMouseOver={(e) => {
            if (!generandoPDF) {
              e.currentTarget.style.backgroundColor = '#00BCD4';
              e.currentTarget.style.color = '#fff';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#fff';
            e.currentTarget.style.color = '#00BCD4';
          }}
        >
          {generandoPDF ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" />
              Generando PDF...
            </>
          ) : (
            <>📄 Descargar PDF</>
          )}
        </button>
      </div>

      {/* Modal Reportar Olvido */}
      <ModalReportarOlvido
        show={showOlvidoModal}
        onClose={() => setShowOlvidoModal(false)}
        tratamientoId={tratamientoId}
        bloquesDisponibles={bloquesHoy || []}
        onSuccess={handleOlvidoSuccess}
      />
    </div>
  );
}
